import { Autocomplete, autocompleteClasses, createFilterOptions, Popper, TextField } from '@mui/material';
import * as React from 'react';

import { DATA_TEST } from '../../enums';
import { IEntityDetails } from '../../types';

import {
  ENTITY_POPPER_ID,
  EntityOptionElement,
  IEntityOptionMuiProps,
  ReactWindowContext,
  ReactWindowContextValueType,
  ReactWindowListBoxComponent
} from './ReactWindowListBoxComponent';

export interface IEntityAutocompleteOption {
  label: string;
  subdomain: string;
  entityType: string;
}

interface IDesktopEntityAutoCompleteSearchProps {
  selectedEntity: IEntityDetails;
  entitledEntities: IEntityDetails[];
  displayEntityNamesOnly?: boolean;
  handleEntitySelect(entity: IEntityDetails | null): void;
}

const BottomAnchoredPopper = (props) => {
  const { anchorEl } = props;

  // Dynamically calculate the max height of the popper + listbox
  // based on the distance from the anchor element to the bottom of the window.
  const anchorElBottomPosition = anchorEl?.getBoundingClientRect().bottom;
  const distanceFromAnchorElToWindowBottom = window.innerHeight - anchorElBottomPosition;
  const maxHeight = distanceFromAnchorElToWindowBottom - 8;

  return (
    <Popper
      // This ID is needed for the ReactWindowListBoxComponent to query for and match match the popper height
      id={ENTITY_POPPER_ID}
      {...props}
      placement="bottom"
      sx={{
        maxHeight,
        height: '100%',
        // The listbox also needs to be height controlled to prevent it from overflowing the popper
        [`& .${autocompleteClasses.listbox}`]: {
          maxHeight
        }
      }}
    />
  );
};

export const DesktopEntityAutoCompleteSearch: React.FC<IDesktopEntityAutoCompleteSearchProps> = ({
  selectedEntity,
  entitledEntities,
  displayEntityNamesOnly,
  handleEntitySelect
}) => {
  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: IEntityAutocompleteOption) => `${option.label} ${option.subdomain} ${option.entityType}`
  });
  const [inputValue, setInputValue] = React.useState('');
  const [autocompleteValue, setAutocompleteValue] = React.useState<IEntityAutocompleteOption | null>(null);

  React.useEffect(() => {
    if (selectedEntity) {
      setAutocompleteValue({
        label: selectedEntity.displayName || selectedEntity.name,
        subdomain: selectedEntity.friendlyId,
        entityType: selectedEntity.entityType
      });
    }
  }, [selectedEntity]);

  const onItemClick = (option) => {
    const selectedEntityOnClick = entitledEntities.find((entity) => entity.friendlyId === option.subdomain);
    handleEntitySelect(selectedEntityOnClick as IEntityDetails);
  };

  const onAutocompleteChange = (newValue) => {
    if (newValue !== null) {
      const selectedEntityOnChange = entitledEntities.find((entity) => entity.friendlyId === newValue.subdomain);
      handleEntitySelect(selectedEntityOnChange as IEntityDetails);
      setAutocompleteValue(newValue);
    } else {
      handleEntitySelect(null);
      setAutocompleteValue(null);
    }
  };

  const formattedEntities: IEntityAutocompleteOption[] = entitledEntities?.map((entity) => ({
    label: entity.displayName || entity.name,
    subdomain: entity.friendlyId,
    entityType: entity.entityType
  }));

  const reactWindowContextValue = React.useMemo<ReactWindowContextValueType>(
    () => ({ handleEntityOptionClick: onItemClick, displayEntityNamesOnly: displayEntityNamesOnly || false }),
    [onItemClick, displayEntityNamesOnly]
  );

  return (
    <ReactWindowContext.Provider value={reactWindowContextValue}>
      <Autocomplete
        id="entity-search-autocomplete"
        options={formattedEntities}
        size="medium"
        filterOptions={filterOptions}
        value={autocompleteValue}
        clearOnEscape={true}
        data-test={DATA_TEST.entity_autocomplete}
        onChange={(_, newValue) => {
          onAutocompleteChange(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(_, onChangeValue) => {
          setInputValue(onChangeValue);
        }}
        includeInputInList={true}
        isOptionEqualToValue={(option, valueToCompare) => option.subdomain === valueToCompare.subdomain}
        renderOption={(props, option) => (
          // Autocomplete's published prop type understates what it actually passes
          // (`data-option-index` and friends), so narrow it to what this list consumes.
          <EntityOptionElement key={option.subdomain} muiProps={props as IEntityOptionMuiProps} option={option} />
        )}
        openOnFocus={true}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Find an entity by name or location type"
            label="Find an entity by name or location type"
            size="medium"
            data-test={DATA_TEST.entity_autocomplete_search_input}
            InputLabelProps={{ shrink: true }}
          />
        )}
        /*
         * `PopperComponent` / `ListboxComponent` rather than `slots.popper` /
         * `slotProps.listbox.component`.
         *
         * The slot props are Material UI v6+ only. On a v5 host they are silently
         * ignored — v5 has no `slots` prop and its `slotProps` has no `listbox` — so the
         * default `ul` renders `renderOption`'s output directly and the virtualization
         * never engages. These two deprecated props are the only spelling both majors
         * honor, and this package is consumed by hosts on either. Do not "modernize"
         * them back without first making the package's Material UI requirement a peer
         * dependency that a v5 host cannot silently satisfy.
         */
        PopperComponent={BottomAnchoredPopper}
        ListboxComponent={ReactWindowListBoxComponent}
      />
    </ReactWindowContext.Provider>
  );
};
